<script>
    import opsChangeInboxService from '@/services/opsChangeInbox.service';
    import Spinner from '@/components/shared/Spinner';
    import CompanySearchSelect from '@/components/shared/CompanySearchSelect';

    export default {
        components: { Spinner, CompanySearchSelect },
        data() {
            return {
                rows: [],
                selectedFilterCompany: null,
                // The filter the rows on screen actually came from, so the empty state can tell
                // "nothing for this company" from "nothing at all" even after the select changes.
                loadedCompanyName: null,
                isLoading: false,
                loadError: null
            };
        },
        mounted() {
            this.load();
        },
        methods: {
            apiError(err, fallback) {
                return err?.data?.message || err?.message || fallback;
            },
            companyFilterId() {
                const id = parseInt(this.selectedFilterCompany?.id, 10);
                return Number.isFinite(id) && id > 0 ? id : undefined;
            },
            formatLocal(value) {
                if (!value) return '—';
                const d = new Date(value);
                if (Number.isNaN(d.getTime())) return value;
                return d.toLocaleString();
            },
            statusText(row) {
                return row.statusLabel || (row.closed ? 'Closed' : 'Open');
            },
            statusClass(row) {
                return row.closed ? 'text-bg-secondary' : 'text-bg-primary';
            },
            async load() {
                this.isLoading = true;
                this.loadError = null;
                const filteredBy = this.selectedFilterCompany?.name || null;
                try {
                    this.rows = (await opsChangeInboxService.listThreads(this.companyFilterId())) || [];
                    this.loadedCompanyName = filteredBy;
                } catch (err) {
                    this.loadError = this.apiError(err, 'Could not load Setup threads.');
                } finally {
                    this.isLoading = false;
                }
            },
            open(row) {
                this.$router.push(`/ops-change-inbox/${encodeURIComponent(row.threadId)}`);
            }
        }
    };
</script>

<template>
    <div class="container-fluid py-3">
        <div class="mb-3">
            <h1 class="h3 mb-1">Setup inbox</h1>
            <p class="text-muted mb-0">
                Every email thread opened with setup@greasebook.com. To choose who may open threads for a
                company, use Setup inbox access on that company in Companies.
            </p>
        </div>

        <div class="row g-3 mb-4">
            <div class="col-md-4">
                <label class="form-label" for="opsThreadCompanyFilter">Company</label>
                <CompanySearchSelect
                    v-model="selectedFilterCompany"
                    input-id="opsThreadCompanyFilter"
                    placeholder="Type at least 3 letters…"
                />
            </div>
            <div class="col-md-2 d-flex align-items-end">
                <button class="btn btn-outline-primary" type="button" :disabled="isLoading" @click="load">
                    Show threads
                </button>
            </div>
        </div>

        <div v-if="loadError" class="alert alert-danger d-flex align-items-center gap-3" role="alert">
            <span class="flex-grow-1">{{ loadError }}</span>
            <button class="btn btn-sm btn-outline-danger flex-shrink-0" type="button" @click="load">
                Try again
            </button>
        </div>

        <Spinner v-if="isLoading" />

        <table v-else class="table table-sm table-hover align-middle">
            <caption class="text-muted small">Times are shown in your local timezone.</caption>
            <thead>
                <tr>
                    <th scope="col">Company</th>
                    <th scope="col">Requester</th>
                    <th scope="col">Subject</th>
                    <th scope="col">Started</th>
                    <th scope="col">Last message</th>
                    <th scope="col">Status</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="row in rows"
                    :key="row.threadId"
                    class="thread-row"
                    tabindex="0"
                    @click="open(row)"
                    @keydown.enter.prevent="open(row)"
                    @keydown.space.prevent="open(row)"
                >
                    <td>{{ row.companyName || row.companyId || '—' }}</td>
                    <td>{{ row.requesterEmail }}</td>
                    <td>{{ row.subject || '—' }}</td>
                    <td>{{ formatLocal(row.startedUtc) }}</td>
                    <td>{{ formatLocal(row.lastMessageUtc) }}</td>
                    <td>
                        <span class="badge" :class="statusClass(row)">{{ statusText(row) }}</span>
                        <span v-if="row.applied" class="badge text-bg-success ms-1">Applied</span>
                    </td>
                </tr>
                <tr v-if="!rows.length">
                    <td colspan="6" class="text-muted">
                        <template v-if="loadedCompanyName">
                            No threads for {{ loadedCompanyName }} yet.
                        </template>
                        <template v-else>
                            No threads yet. One appears here as soon as somebody emails setup@greasebook.com.
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style lang="scss" scoped>
.thread-row {
    cursor: pointer;
}

.thread-row:focus-visible {
    outline: 2px solid var(--bs-primary);
    outline-offset: -2px;
}
</style>
